/*
 * Copyright © 2020-2024 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 成员位置筛选组件
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-12-04 15:52:38
 */

import React, { useState, useCallback, useMemo } from 'react'
import { Select, Form, Checkbox } from 'antd'
import DepartmentMemberPicker from '../../../components/DepartmentMemberPicker'
import { useAppSelector } from '../../../../app/hooks'
import sensors from '../../../../utils/sensors'
import './index.css'

interface FilterProps {
  handleChangeFilter: (filterData: any) => void
  filterData: {
    departmentIDList: string[]
    activeStatus: number | undefined
    userIDList: string[]
    selectedDepartments: any[]
    selectedNormalUserIDs: string[]
    selectedDepartmentUserIDs: string[]
  }
  sensorsClick: (actionType: string, params?: { [key: string]: string }) => void
}

const Filter: React.FC<FilterProps> = ({
  handleChangeFilter,
  filterData,
  sensorsClick
}) => {
  const [memberPickerVisible, setMemberPickerVisible] = useState(false)

  const pickerUserCount = useAppSelector(
    (state) => state.organization.pickerOrgUserCount
  )

  // 计算已选择的成员数量
  const pickedMembersNum = useMemo(() => {
    const departmentUserCount = filterData.selectedDepartments.reduce(
      (acc, department) => acc + (department.num || 0),
      0
    )
    const normalUserCount = filterData.selectedNormalUserIDs.length
    const total = departmentUserCount + normalUserCount
    const hasSelection =
      filterData.selectedDepartments.length > 0 ||
      filterData.selectedNormalUserIDs.length > 0

    if (
      (!hasSelection && total === 0) ||
      total === pickerUserCount ||
      filterData.selectedDepartments.some((department) => department.id === '0')
    ) {
      return pickerUserCount
    }
    return total
  }, [filterData, pickerUserCount])

  // 成员选择器显示值
  const memberPickerValue = useMemo(() => {
    if (pickedMembersNum === pickerUserCount) {
      return '全部成员'
    }
    return `已选${pickedMembersNum}人`
  }, [pickedMembersNum, pickerUserCount])

  // 处理活跃状态变化
  const handleActiveStatusChange = useCallback(
    (checked: boolean) => {
      const newFilterData = {
        ...filterData,
        activeStatus: checked ? 1 : undefined // 勾选时为活跃状态(1)，未勾选时为undefined
      }
      handleChangeFilter(newFilterData)

      // 添加活跃状态埋点
      if (checked) {
        sensorsClick('openActiveNow')
      } else {
        sensorsClick('closeActiveNow')
      }
    },
    [filterData, handleChangeFilter, sensorsClick]
  )

  // 处理成员选择器确认
  const handleMemberPickerOk = useCallback(
    (params) => {
      const {
        selectedNormalUserIDs,
        selectedDepartmentUserIDs,
        selectedDepartments
      } = params

      // 构建部门ID列表
      const departmentIDList = selectedDepartments.map((dept) => dept.id)

      // 构建用户ID列表（包含普通用户和部门用户）
      const userIDList = [
        ...selectedNormalUserIDs,
        ...selectedDepartmentUserIDs
      ]

      const newFilterData = {
        ...filterData,
        departmentIDList,
        userIDList,
        selectedDepartments,
        selectedNormalUserIDs,
        selectedDepartmentUserIDs
      }

      handleChangeFilter(newFilterData)
      setMemberPickerVisible(false)

      // 埋点统计
      // sensors.track('web_workgroup_memberLocation_page_click', {
      //   clickItem: 'filterMember',
      //   selectedDepartmentCount: selectedDepartments.length,
      //   selectedUserCount: userIDList.length,
      //   pagePath: window.location.pathname
      // })
    },
    [filterData, handleChangeFilter]
  )

  // 处理成员选择器取消
  const handleMemberPickerCancel = useCallback(() => {
    setMemberPickerVisible(false)
  }, [])

  return (
    <div className="member-location-filter">
      <Form layout="inline" className="filter-form">
        <Form.Item label=" 仅展示当前活跃 ">
          <Checkbox
            checked={filterData.activeStatus === 1}
            onChange={(e) => handleActiveStatusChange(e.target.checked)}
          />
        </Form.Item>

        <Form.Item label="筛选成员">
          <Select
            style={{ width: 200 }}
            value={memberPickerValue}
            open={false}
            onDropdownVisibleChange={() => {
              // 添加筛选成员埋点
              sensorsClick('searchMember')
              setMemberPickerVisible(true)
            }}
            placeholder="选择成员"
          />
        </Form.Item>
      </Form>

      <DepartmentMemberPicker
        open={memberPickerVisible}
        title="选择成员"
        filterObj={{
          departments: filterData.selectedDepartments,
          members: filterData.selectedNormalUserIDs,
          userDepartmentIDs: filterData.selectedDepartmentUserIDs
        }}
        onOk={handleMemberPickerOk}
        onCancel={handleMemberPickerCancel}
      />
    </div>
  )
}

export default Filter
